import { useEffect, useState } from 'react';
import { Check, CheckCircle2, RotateCcw, X as XIcon } from 'lucide-react';
import { Button } from '../../components/Button';
import { LoadingState, ErrorState } from '../../components/LoadingState';
import { useGenerateFlashcards } from '../../api/queries';
import { Card } from './Card';
import styles from './FlashcardsView.module.css';

export type Flashcard = {
  id: string;
  front: string;
  back: string;
};

type Verdict = 'know' | 'dont-know';
type Result = { card: Flashcard; verdict: Verdict };

export function FlashcardsView() {
  const mutation = useGenerateFlashcards();
  const [deck, setDeck] = useState<Flashcard[]>([]);
  const [results, setResults] = useState<Result[]>([]);

  // kick off on mount
  useEffect(() => {
    if (mutation.data || mutation.isPending) return;
    mutation.mutate({ count: 5 });
    // we intentionally run once; mutation is stable for the lifetime
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // when data lands, seed the deck
  useEffect(() => {
    if (!mutation.data) return;
    const list = (mutation.data as any)?.content?.flashcards ?? [];
    const mapped: Flashcard[] = list.map((f: any, i: number) => ({
      id: `fc-${i}`,
      front: f.front ?? f.question ?? '',
      back: f.back ?? f.answer ?? ''
    }));
    setDeck(mapped);
    setResults([]);
  }, [mutation.data]);

  if (mutation.isPending || (!mutation.data && !mutation.error))
    return (
      <LoadingState
        label="Generando tarjetas didácticas…"
        hint="Convirtiendo el módulo en un mazo de repaso"
      />
    );
  if (mutation.error)
    return <ErrorState error={mutation.error} onRetry={() => mutation.mutate({ count: 5 })} />;

  const dismiss = (verdict: Verdict) => {
    setDeck((prev) => {
      if (prev.length === 0) return prev;
      const [top, ...rest] = prev;
      setResults((r) => [...r, { card: top, verdict }]);
      return rest;
    });
  };

  const reset = () => {
    mutation.mutate({ count: 5 });
  };

  const total = (mutation.data as any)?.content?.flashcards?.length ?? 0;
  const seen = results.length;
  const knowCount = results.filter((r) => r.verdict === 'know').length;

  if (total > 0 && deck.length === 0) {
    const perfect = knowCount === total;
    const missed = total - knowCount;
    return (
      <div className={styles.view}>
        <div className={styles.summary}>
          <div className={styles.scoreMedal} data-perfect={perfect}>
            <CheckCircle2 size={28} strokeWidth={2} />
          </div>
          <h3 className={styles.summaryTitle}>
            {perfect ? '¡Perfecto!' : '¡Buen trabajo!'}
          </h3>
          <p className={styles.summaryBody}>
            {perfect
              ? `Te sabías las ${total} tarjetas.`
              : `Te sabías ${knowCount} de ${total}.`}
          </p>

          <div className={styles.statsRow}>
            <div className={styles.statBox}>
              <span className={styles.statLabel}>Acertaste</span>
              <span className={`${styles.statNum} ${styles.statKnow}`}>{knowCount}</span>
            </div>
            <div className={styles.statBox}>
              <span className={styles.statLabel}>Por repasar</span>
              <span className={`${styles.statNum} ${styles.statMiss}`}>{missed}</span>
            </div>
          </div>

          <Button onClick={reset} variant="primary">
            <RotateCcw size={16} strokeWidth={2.4} />
            Empezar de nuevo
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.view}>
      <div className={styles.progress}>
        <div className={styles.progressBar}>
          <div className={styles.progressFill} style={{ width: `${(seen / total) * 100}%` }} />
        </div>
        <span className={styles.progressLabel}>
          {seen} / {total}
        </span>
      </div>

      <div className={styles.deck}>
        {deck.slice(0, 3).map((card, idx) => (
          <Card
            key={card.id}
            card={card}
            offset={idx}
            isTop={idx === 0}
            onDismiss={dismiss}
          />
        ))}
      </div>

      <div className={styles.actions}>
        <button
          type="button"
          className={`${styles.actionBtn} ${styles.actionNope}`}
          onClick={() => dismiss('dont-know')}
          aria-label="No me la sé"
        >
          <XIcon size={22} strokeWidth={2.6} />
        </button>
        <button
          type="button"
          className={`${styles.actionBtn} ${styles.actionKnow}`}
          onClick={() => dismiss('know')}
          aria-label="Me la sé"
        >
          <Check size={22} strokeWidth={2.6} />
        </button>
      </div>
    </div>
  );
}
